import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  EditableToken,
  SectionContextProvider,
  useBlockSlot,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

const bgDef: SlotGroupDefinition = {
  label: 'Background Photo',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[14].image' },
  },
  generator: {
    prompt: 'A bright, uncluttered photo (pets, outdoor scenery, or a lifestyle moment) with open space on the right for a form card.',
    context: 'Full-bleed background of the "Get in Touch" lead form section at the bottom of the home page. The form card covers the right half.',
  },
};

/** Lead form card floating over a full-bleed photo. */
export function ContactCTA() {
  const bg = useSlotGroup('contactcta.background', bgDef);
  const form = useBlockSlot('contactcta.form', { defaultValue: 'multihub-lead-form' });

  return (
    <section className="js-contact relative isolate overflow-hidden bg-muted py-12 md:py-20">
      <EditableSlotGroup groupPath="contactcta.background" definition={bgDef}>
        <div className="js-parallax absolute inset-0 -z-10">
          <VibesitesImage url={bg.image || ''} alt="" sizes="100vw" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-black/10" />
        </div>
      </EditableSlotGroup>

      <div className="mx-auto flex max-w-[1280px] justify-center px-4 sm:px-6 md:justify-end lg:px-10">
        <div className="js-reveal-right w-full max-w-[600px] md:w-[52%]">
          <EditableToken path="contactcta.form" type="block" label="Lead Form" as="div">
            {form ? (
              <SectionContextProvider sectionId={form.sectionId}>
                <form.BlockComponent sectionId={form.sectionId} />
              </SectionContextProvider>
            ) : (
              <div className="rounded-lg bg-background p-8 text-center text-[color:var(--vibe-color-text-muted)] shadow-lg">No form selected</div>
            )}
          </EditableToken>
        </div>
      </div>

    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ContactCTA',
  displayName: 'Get in Touch',
  tokens: {
    'contactcta.form': {
      label: 'Form',
      type: 'block',
      defaultValue: 'multihub-lead-form',
      acceptedBlockCategories: ['Forms'],
    },
  },
  slotGroups: {
    'contactcta.background': bgDef,
  },
};

(ContactCTA as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ContactCTA;
